import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/card';
import { useMyTeams } from './useMyTeams';
import { useHomeScreenTeams } from './useHomeScreenTeams';
import { withModuleDefaults } from './utils/moduleUtils';
import { CalendarLink } from '../ui/CalendarButton';

const compactButtonStyle = {
  padding: '0.32rem 0.6rem',
  fontSize: 13,
  borderRadius: 8,
  minHeight: 32,
  boxShadow: 'none',
};
const DASHBOARD_SCROLL_MAX_HEIGHT = 260;

export default function HomeScreenTeamsCard() {
  const { teams, loading: teamsLoading, reloadTeams } = useMyTeams();
  const {
    homeTeamIds,
    loading: homeTeamsLoading,
    setHomeTeamOrder,
    reload: reloadHomeTeams,
  } = useHomeScreenTeams();
  const [sortMode, setSortMode] = useState('custom'); // custom | alpha
  const [editOpen, setEditOpen] = useState(false);
  const [draftOrderIds, setDraftOrderIds] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [hasResolvedOnce, setHasResolvedOnce] = useState(false);
  const loading = teamsLoading || homeTeamsLoading;

  useEffect(() => {
    if (!loading) setHasResolvedOnce(true);
  }, [loading]);

  const teamsById = useMemo(
    () => new Map((teams || []).map((team) => [team.id, team])),
    [teams]
  );

  const pinnedTeamsInUserOrder = useMemo(() => {
    return homeTeamIds
      .map((id) => teamsById.get(id))
      .filter(Boolean);
  }, [homeTeamIds, teamsById]);
  const hasPinnedTeams = pinnedTeamsInUserOrder.length > 0;

  const pinnedTeams = useMemo(() => {
    if (sortMode !== 'alpha') return pinnedTeamsInUserOrder;
    return [...pinnedTeamsInUserOrder].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  }, [pinnedTeamsInUserOrder, sortMode]);

  const openEditModal = () => {
    setDraftOrderIds(pinnedTeamsInUserOrder.map((team) => team.id).filter(Boolean));
    setEditError('');
    setEditOpen(true);
  };

  const moveDraft = (index, direction) => {
    const nextIndex = index + direction;
    setDraftOrderIds((prev) => {
      if (index < 0 || nextIndex < 0 || index >= prev.length || nextIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(nextIndex, 0, moved);
      return next;
    });
  };

  const removeDraft = (teamId) => {
    setDraftOrderIds((prev) => prev.filter((id) => id !== teamId));
  };

  const saveDraft = async () => {
    setSavingEdit(true);
    setEditError('');
    try {
      const result = await setHomeTeamOrder(draftOrderIds);
      if (!result?.ok) {
        setEditError('Could not save your home club order. Please try again.');
        return;
      }
      setSortMode('custom');
      await Promise.all([
        reloadHomeTeams?.(),
        reloadTeams?.(),
      ]);
      setEditOpen(false);
    } finally {
      setSavingEdit(false);
    }
  };

  const draftTeams = useMemo(
    () => draftOrderIds.map((teamId) => teamsById.get(teamId)).filter(Boolean),
    [draftOrderIds, teamsById]
  );

  if (loading && !hasPinnedTeams && !hasResolvedOnce) {
    return (
      <Card title="Home Clubs" className="dashboard-card" style={{ minHeight: 0 }}>
        <p style={{ color: 'var(--clr-text-muted)' }}>Loading your home clubs...</p>
      </Card>
    );
  }

  if (!hasPinnedTeams) return null;

  const shouldScroll = pinnedTeams.length > 3;

  return (
    <Card className="dashboard-card" style={{ minHeight: 0 }}>
      <div style={{ display: 'grid', gap: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '1.45rem', lineHeight: 1.15, fontWeight: 700 }}>Home Clubs</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label htmlFor="home-club-sort" style={{ fontWeight: 600, fontSize: 13 }}>
              Sort
            </label>
            <select
              id="home-club-sort"
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value)}
              style={{
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                padding: '0.3rem 0.5rem',
                fontSize: 13,
                background: '#fff',
                color: '#0f172a',
              }}
            >
              <option value="alpha">A-Z</option>
              <option value="custom">My order</option>
            </select>
            <button
              type="button"
              className="button button-secondary button--sm"
              onClick={openEditModal}
              style={compactButtonStyle}
            >
              Edit
            </button>
          </div>
        </div>
        <p style={{ color: 'var(--clr-text-muted)', margin: 0, fontSize: 13 }}>
          Clubs you pinned from Clubs.
        </p>
      </div>
      <div
        style={{
          display: 'grid',
          gap: 6,
          marginTop: 8,
          maxHeight: shouldScroll ? DASHBOARD_SCROLL_MAX_HEIGHT : 'none',
          overflowY: shouldScroll ? 'auto' : 'visible',
          paddingRight: shouldScroll ? 4 : 0,
        }}
      >
        {pinnedTeams.map((team) => {
          const eventsEnabled = withModuleDefaults(team.modules).events?.enabled;
          return (
            <div
              key={team.id}
              style={{
                border: '1px solid var(--clr-border, #e5e7eb)',
                borderRadius: 9,
                padding: '0.45rem 0.6rem',
                background: '#fff',
                display: 'grid',
                gap: 6,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>{team.name || 'Club'}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Link
                  to={`/teams/${team.clubUrlSlug || team.id}`}
                  className="button button-secondary button--sm"
                  style={compactButtonStyle}
                >
                  Open
                </Link>
                {eventsEnabled && (
                  <CalendarLink
                    to={`/teams/${team.id}/calendar`}
                    compact
                  >
                    Calendar
                  </CalendarLink>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editOpen && (
        <div className="modal-backdrop" onClick={() => !savingEdit && setEditOpen(false)}>
          <div
            className="modal-content"
            onClick={(event) => event.stopPropagation()}
            style={{ maxWidth: 34 * 16, width: '100%' }}
          >
            <h2 className="modal-title">Edit Home Clubs</h2>
            <p className="text-muted" style={{ marginTop: 0 }}>
              Reorder your pinned clubs and remove any you no longer want on Home.
            </p>

            <div
              style={{
                display: 'grid',
                gap: 8,
                maxHeight: 300,
                overflowY: 'auto',
                paddingRight: 4,
              }}
            >
              {draftTeams.length === 0 && (
                <div className="text-muted">No pinned clubs selected.</div>
              )}
              {draftTeams.map((team, index) => (
                <div
                  key={team.id}
                  style={{
                    border: '1px solid var(--clr-border, #e5e7eb)',
                    borderRadius: 8,
                    padding: '0.5rem 0.6rem',
                    background: '#fff',
                    display: 'grid',
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '36px minmax(0, 1fr) 36px',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <button
                      type="button"
                      className="button button-secondary button--sm"
                      style={{ ...compactButtonStyle, width: 36, minWidth: 36, padding: 0 }}
                      disabled={index === 0 || savingEdit}
                      onClick={() => moveDraft(index, -1)}
                      aria-label={`Move ${team.name || 'club'} up`}
                    >
                      ↑
                    </button>
                    <div
                      style={{
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {team.name || 'Club'}
                    </div>
                    <button
                      type="button"
                      className="button button-secondary button--sm"
                      style={{ ...compactButtonStyle, width: 36, minWidth: 36, padding: 0 }}
                      disabled={index === draftTeams.length - 1 || savingEdit}
                      onClick={() => moveDraft(index, 1)}
                      aria-label={`Move ${team.name || 'club'} down`}
                    >
                      ↓
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="button button-ghost button--sm"
                      style={compactButtonStyle}
                      disabled={savingEdit}
                      onClick={() => removeDraft(team.id)}
                    >
                      Remove from Home
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {editError && (
              <div className="text-error" style={{ marginTop: 10 }}>
                {editError}
              </div>
            )}

            <div className="form-actions" style={{ marginTop: 14 }}>
              <button
                type="button"
                className="button button-secondary button--sm"
                style={compactButtonStyle}
                disabled={savingEdit}
                onClick={() => setEditOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button button-primary button--sm"
                style={compactButtonStyle}
                disabled={savingEdit}
                onClick={saveDraft}
              >
                {savingEdit ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
